// filepath: src/services/videoUrl/videoUrlService.ts
/**
 * Vimeo video URL sanitization & verification.
 *
 * Why this exists:
 *   When editors copy a Vimeo URL via "Share -> Copy link" the URL contains
 *   tracking params like `share=copy`, `utm_*`, `fbclid`, etc. These are NOT
 *   valid Vimeo embed parameters and can interfere with the player's session
 *   token (psid) generation, leading to CDN XHR failures
 *   (e.g. `XHR failed loading: GET "https://skyfire.vimeocdn.com/..."`).
 *
 * Strategy:
 *   1. `sanitizeVideoUrl` - strip known invalid params, normalize host.
 *   2. `verifyVideoUrl`   - call Vimeo oEmbed (no auth required) to confirm
 *      the video exists and is embeddable; also returns thumbnail metadata.
 */

const INVALID_PARAMS = new Set<string>([
  'share',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'fbclid',
  'gclid',
  '_hsenc',
  '_hsmi',
  'mc_cid',
  'mc_eid',
]);

export interface SanitizeResult {
  original: string;
  sanitized: string;
  changed: boolean;
  removedParams: string[];
  videoId: string | null;
  isVimeo: boolean;
}

export interface VerifyResult {
  ok: boolean;
  status: 'ok' | 'not-found' | 'forbidden' | 'invalid-url' | 'network-error';
  message: string;
  thumbnailUrl?: string;
  title?: string;
  videoId?: string;
}

/**
 * Extract numeric Vimeo video ID from a Vimeo URL (player or canonical).
 * Returns null if not a recognizable Vimeo URL.
 */
export function extractVimeoVideoId(url: string): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (!host.endsWith('vimeo.com')) return null;
    // Match /video/{id} or /{id} (followed by optional /something)
    const match = parsed.pathname.match(/\/(?:video\/)?(\d+)(?:\/|$)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Strip tracking / non-embed params from a video URL.
 * Non-Vimeo URLs are returned unchanged (only param stripping applied).
 */
export function sanitizeVideoUrl(rawUrl: string | null | undefined): SanitizeResult {
  const original = (rawUrl ?? '').trim();
  if (!original) {
    return {
      original: '',
      sanitized: '',
      changed: false,
      removedParams: [],
      videoId: null,
      isVimeo: false,
    };
  }

  let parsed: URL;
  try {
    parsed = new URL(original);
  } catch {
    return {
      original,
      sanitized: original,
      changed: false,
      removedParams: [],
      videoId: null,
      isVimeo: false,
    };
  }

  const removed: string[] = [];
  for (const key of Array.from(parsed.searchParams.keys())) {
    if (INVALID_PARAMS.has(key)) {
      removed.push(key);
      parsed.searchParams.delete(key);
    }
  }

  const isVimeo = parsed.hostname.toLowerCase().endsWith('vimeo.com');
  const videoId = isVimeo ? extractVimeoVideoId(parsed.toString()) : null;

  // Note: We deliberately do NOT add embed params (background, autoplay, ...)
  // here. The backend's `ensure_video_url_params()` handles that on read.
  // We only strip; the canonical embed shape is the backend's responsibility.

  const sanitized = parsed.toString();
  return {
    original,
    sanitized,
    changed: sanitized !== original,
    removedParams: removed,
    videoId,
    isVimeo,
  };
}

/**
 * Verify a Vimeo video exists and is embeddable using the public oEmbed API.
 * No auth required. Returns thumbnail URL on success.
 *
 * For non-Vimeo URLs, returns ok=true (we can't verify them generically).
 */
export async function verifyVideoUrl(url: string): Promise<VerifyResult> {
  const trimmed = (url ?? '').trim();
  if (!trimmed) {
    return { ok: false, status: 'invalid-url', message: 'URL is empty' };
  }

  const videoId = extractVimeoVideoId(trimmed);
  if (!videoId) {
    // Non-Vimeo URL — can't verify generically; trust it.
    return { ok: true, status: 'ok', message: 'Non-Vimeo URL (not verified)' };
  }

  // Extract the unlisted-video hash (`h=...` query param, or `/{id}/{hash}`
  // path segment on canonical vimeo.com URLs). This is REQUIRED for oEmbed
  // lookups of unlisted/private videos — without it, Vimeo returns 404 even
  // though the embed itself works fine on the public site.
  let hash: string | null = null;
  try {
    const parsed = new URL(trimmed);
    hash = parsed.searchParams.get('h');
    if (!hash) {
      // Canonical form: https://vimeo.com/{id}/{hash}
      const segs = parsed.pathname.split('/').filter(Boolean);
      const idx = segs.indexOf(videoId);
      if (idx >= 0 && segs[idx + 1] && /^[a-f0-9]{6,}$/i.test(segs[idx + 1])) {
        hash = segs[idx + 1];
      }
    }
  } catch {
    // ignore — verified below
  }

  // Build canonical vimeo.com URL for oEmbed lookup. For unlisted videos
  // include the hash as a path segment: https://vimeo.com/{id}/{hash}
  const canonicalUrl = hash
    ? `https://vimeo.com/${videoId}/${hash}`
    : `https://vimeo.com/${videoId}`;
  const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(canonicalUrl)}`;

  try {
    const res = await fetch(oembedUrl);
    if (res.status === 404) {
      return {
        ok: false,
        status: 'not-found',
        message: 'Vimeo video not found (404). Check the URL or video privacy.',
        videoId,
      };
    }
    if (res.status === 403) {
      return {
        ok: false,
        status: 'forbidden',
        message: 'Embed is disabled for this video (403). Update privacy settings on Vimeo.',
        videoId,
      };
    }
    if (!res.ok) {
      return {
        ok: false,
        status: 'network-error',
        message: `Vimeo returned HTTP ${res.status}.`,
        videoId,
      };
    }
    const data = (await res.json()) as {
      thumbnail_url?: string;
      title?: string;
    };
    return {
      ok: true,
      status: 'ok',
      message: 'Verified.',
      thumbnailUrl: data.thumbnail_url,
      title: data.title,
      videoId,
    };
  } catch (err) {
    return {
      ok: false,
      status: 'network-error',
      message: err instanceof Error ? err.message : 'Network error contacting Vimeo.',
      videoId,
    };
  }
}

/**
 * Convenience helper: Vumbnail.com thumbnail URL (no API call).
 * Useful for fast preview rendering. Returns empty string if no Vimeo ID.
 */
export function getVumbnailUrl(
  vimeoUrl: string,
  size: 'small' | 'medium' | 'large' | '' = ''
): string {
  const id = extractVimeoVideoId(vimeoUrl);
  if (!id) return '';
  const suffix = size ? `_${size}` : '';
  return `https://vumbnail.com/${id}${suffix}.jpg`;
}
